import * as React from 'react';
import axios from 'axios';
import { getCookies } from "cookies-next";
import Carousel from 'react-multi-carousel';
import 'react-multi-carousel/lib/styles.css';

import { config } from '../../data/config';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { Input } from 'rsuite';
import ImageUploaders from '../tools/uploaderimages';

type LanguageCode = 'ku' | 'fa' | 'en' | 'ar' | 'tr';
export default function TissSlider({ data, reload, activeLang = 'en' }: { data: any; reload: () => void; activeLang?: LanguageCode }) {
  const { t } = useTranslation();
  const cookie = getCookies();

  // Initial data
  const initialText = data?.data?.html || '';

  // State
  const [isEditing, setIsEditing] = React.useState(false);
  const [listimage, setlistimage] = React.useState<any[]>([]);
  const [text, setText] = React.useState(initialText);
  const [saving, setSaving] = React.useState(false);
  const [hasChanges, setHasChanges] = React.useState(false);
  const [loading, setLoading] = React.useState(true);
  const [previewImage, setPreviewImage] = React.useState<string | null>(null);

  // Track changes
  React.useEffect(() => {
    setHasChanges(text !== initialText);
  }, [text, initialText]);

  // Carousel responsive config
  const responsive = {
    superLargeDesktop: {
      breakpoint: { max: 4000, min: 3000 },
      items: 5
    },
    desktop: {
      breakpoint: { max: 3000, min: 1024 },
      items: 3
    },
    tablet: {
      breakpoint: { max: 1024, min: 464 },
      items: 2
    },
    mobile: {
      breakpoint: { max: 464, min: 0 },
      items: 1
    }
  };

  // Load images
  const loaditem = async () => {
    try {
      setLoading(true);
      const response = await axios({
        method: "get",
        url: `${config.url}/v1/dashboard/filemanager?id=${data.id}&component=ImageSlider`,
        headers: {
          Authorization: 'Bearer ' + cookie['token'],
          "Content-Type": "multipart/form-data"
        },
      });
      setlistimage(response.data.data || []);
    } catch (error) {
      console.error('Failed to load images:', error);
      Swal.fire({
        title: t('Error loading images'),
        icon: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    loaditem();
  }, []);

  // Save handler
  const saveContent = async () => {
    if (!data?._id) return;

    setSaving(true);
    try {
      await axios.put(
        `${config.url}/v1/dashboard/component/texteditor/${data._id}`,
        { html: text },
        {
          headers: { Authorization: 'Bearer ' + cookie['token'] },
        }
      );
      Swal.fire({
        title: t('Saved successfully'),
        icon: 'success',
        timer: 1200,
        showConfirmButton: false,
      });
      setIsEditing(false);
      setHasChanges(false);
      reload();
    } catch (error) {
      console.error('Save failed:', error);
      Swal.fire({
        title: t('Error saving'),
        icon: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  // Cancel handler with confirmation if there are changes
  const handleCancel = () => {
    if (hasChanges) {
      Swal.fire({
        title: t('Discard changes?'),
        text: t('You have unsaved changes. Are you sure you want to cancel?'),
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: t('Yes, discard'),
        cancelButtonText: t('No, keep editing'),
      }).then((result) => {
        if (result.isConfirmed) {
          setText(initialText);
          setIsEditing(false);
          setHasChanges(false);
        }
      });
    } else {
      setText(initialText);
      setIsEditing(false);
    }
  };

  // Handle Escape key to close preview
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && previewImage) {
        setPreviewImage(null);
      }
    };
    if (previewImage) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [previewImage]);

  // Determine if we have content
  const hasContent = listimage.length > 0 || initialText;

  // Empty state
  if (!hasContent && !isEditing && !loading) {
    return (
      <div
        className="w-full p-4 flex flex-col items-center justify-center cursor-pointer hover:bg-blue-50/30 bg-white transition-all duration-300 group"
        onClick={() => setIsEditing(true)}
        dir="rtl"
      >
        <div className="bg-blue-100 rounded-full p-4 mb-4 group-hover:bg-blue-200 transition-colors duration-300">
          <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </div>
        <span className="text-gray-600 text-sm font-medium group-hover:text-blue-600 transition-colors duration-300">
          {t('Click to add slider')}
        </span>
        <span className="text-gray-400 text-xs mt-1">
          {t('Upload images to create a slider')}
        </span>
      </div>
    );
  }

  // Preview Mode
  if (!isEditing) {
    return (
      <>
        <div className="w-full bg-white transition-all duration-300 group relative" dir="rtl">
          <div className="p-0.5 relative">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            ) : listimage.length > 0 ? (
              <div className="relative rounded-lg overflow-hidden bg-gray-50">
                <Carousel
                  responsive={responsive}
                  infinite={listimage.length > 3}
                  autoPlay={true}
                  autoPlaySpeed={3000}
                  keyBoardControl={true}
                  customTransition="all .5"
                  transitionDuration={500}
                  containerClass="carousel-container"
                  itemClass="carousel-item-padding-40-px"
                  showDots={listimage.length > 1}
                  arrows={listimage.length > 1}
                >
                  {listimage.map((item: any, index: number) => (
                    <div key={index} className="p-2">
                      <div className="relative rounded-lg overflow-hidden bg-white border border-gray-200 hover:border-blue-400 transition-all duration-200 group/image shadow-sm hover:shadow-md">
                        <img
                          src={`${config.fronturl}/${item.url}`}
                          alt={text || t('Slider image')}
                          className="w-full h-56 object-cover cursor-pointer"
                          onClick={() => setPreviewImage(`${config.fronturl}/${item.url}`)}
                          onError={(e) => {
                            e.currentTarget.src = '/placeholder-image.jpg';
                            e.currentTarget.className = 'w-full h-56 object-cover opacity-50';
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover/image:bg-black/5 transition-all duration-300 pointer-events-none"></div>
                        {/* Image number badge */}
                        <div className="absolute top-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full opacity-0 group-hover/image:opacity-100 transition-opacity duration-200">
                          {index + 1} / {listimage.length}
                        </div>
                      </div>
                    </div>
                  ))}
                </Carousel>
                {text && (
                  <div className="mt-3 text-sm text-gray-600 text-right px-2">
                    <span className="text-gray-500 text-xs">{t('Alt text')}:</span>
                    <span className="ml-1">{text}</span>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <div className="flex flex-col items-center">
                  <svg className="w-12 h-12 mb-2 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p className="text-sm">{t('No images in slider')}</p>
                </div>
              </div>
            )}

            {/* Action Buttons - Appear on hover */}
            {listimage.length > 0 && (
              <div className="absolute top-2 left-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10 flex gap-2">
                <button
                  onClick={() => {
                    if (listimage.length > 0) {
                      setPreviewImage(`${config.fronturl}/${listimage[0].url}`);
                    }
                  }}
                  className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                  {t('Preview')}
                </button>
                <button
                  onClick={() => setIsEditing(true)}
                  className="bg-white border border-gray-300 rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-blue-50 hover:border-blue-400 hover:text-blue-600 shadow-md hover:shadow-lg transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  {t('Edit Slider')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Preview Modal */}
        {previewImage && (
          <div
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
            onClick={() => setPreviewImage(null)}
          >
            <div className="relative max-w-7xl max-h-[90vh] w-full h-full flex items-center justify-center">
              <button
                onClick={() => setPreviewImage(null)}
                className="absolute top-4 right-4 bg-white/90 hover:bg-white rounded-full p-2 shadow-lg transition-all duration-200 z-10"
              >
                <svg className="w-6 h-6 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              <img
                src={previewImage}
                alt={text || t('Preview')}
                className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()}
                onError={(e) => {
                  e.currentTarget.src = '/placeholder-image.jpg';
                  e.currentTarget.className = 'max-w-full max-h-full object-contain opacity-50 rounded-lg';
                }}
              />
              {text && (
                <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-lg text-sm max-w-2xl text-center">
                  {text}
                </div>
              )}
            </div>
          </div>
        )}
      </>
    );
  }

  // Edit Mode
  return (
    <div className="w-full bg-white overflow-hidden transition-all duration-300" dir="rtl">
      <div className="p-0.5">
        <div className="space-y-5">
          {/* Image Uploader */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2 text-right">
              {t('Upload Images')}
            </label>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 hover:border-gray-300 transition-colors duration-200">
              <ImageUploaders reload={loaditem} count={60} id={data.id} component={'ImageSlider'} />
            </div>
            {listimage.length > 0 && (
              <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between mb-3">
                  <p className="text-xs text-gray-500 text-right font-medium">
                    {t('Preview')} ({listimage.length} {t('images')})
                  </p>
                  <span className="text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded-full">
                    {listimage.length} {t('items')}
                  </span>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
                  {listimage.slice(0, 12).map((item: any, index: number) => (
                    <div 
                      key={index} 
                      className="relative rounded-lg overflow-hidden bg-white border border-gray-200 hover:border-blue-400 transition-all duration-200 group/thumb cursor-pointer"
                      onClick={() => setPreviewImage(`${config.fronturl}/${item.url}`)}
                    >
                      <img
                        src={`${config.fronturl}/${item.url}`}
                        alt={`${t('Image')} ${index + 1}`}
                        className="w-full h-28 object-cover"
                        onError={(e) => {
                          e.currentTarget.src = '/placeholder-image.jpg';
                          e.currentTarget.className = 'w-full h-28 object-cover opacity-50';
                        }}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover/thumb:bg-black/10 transition-all duration-200"></div>
                      <div className="absolute top-1 right-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded opacity-0 group-hover/thumb:opacity-100 transition-opacity duration-200">
                        {index + 1}
                      </div>
                    </div>
                  ))}
                </div>
                {listimage.length > 12 && (
                  <p className="text-xs text-gray-500 mt-3 text-right">
                    +{listimage.length - 12} {t('more images')} - {t('Click thumbnails to preview')}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Alt Text */}
          <div>
            <label htmlFor="slider-alt-text" className="block text-sm font-medium text-gray-700 mb-2 text-right">
              {t('Alt Text')}
            </label>
            <Input
              id="slider-alt-text"
              value={text}
              onChange={(value) => setText(value || '')}
              placeholder={t('Enter alt text for slider images...')}
              className="w-full"
              style={{
                padding: '10px 16px',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                textAlign: 'right',
              }}
            />
            <p className="mt-2 text-xs text-gray-500 text-right">
              {t('This text will be used for all slider images')}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-6 flex gap-3 justify-end items-center">
          {hasChanges && (
            <span className="text-xs text-orange-600 bg-orange-100 px-3 py-1.5 rounded-full font-medium">
              {t('Unsaved changes')}
            </span>
          )}
          <button
            type="button"
            onClick={handleCancel}
            disabled={saving}
            className="px-5 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 hover:border-gray-400 text-sm font-medium text-gray-700 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            {t('Cancel')}
          </button>
          <button
            type="button"
            onClick={saveContent}
            disabled={saving || !hasChanges}
            className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium transition-all duration-200 flex items-center gap-2 shadow-md hover:shadow-lg"
          >
            {saving ? (
              <>
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>{t('Saving...')}</span>
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>{t('Save')}</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
